import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ReservationRecord,
  RestaurantConfig,
} from 'src/app/shared/interfaces/restaurant.interface';
import { RestaurantService } from 'src/app/shared/services/restaurant/restaurant.service';

type BlockedDate = {
  id: number;
  created_when: string;
  is_deleted: boolean;
  deleted_at: string | null;
  date: string; // "YYYY-MM-DD"
  block_type: 'EVENT' | 'HOLIDAY' | 'NO_SERVICE' | string;
  reason: string;
  author: number;
};

@Component({
  selector: 'app-restaurant-send-menus-dialog',
  imports: [
    CommonModule,
    MatDivider,
    MatDialogModule,
    MatButtonModule,
    DatePipe,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './restaurant-send-menus-dialog.component.html',
  styleUrls: ['./restaurant-send-menus-dialog.component.css', '../../../styles/layout.css'],
})
export class RestaurantSendMenusDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<RestaurantSendMenusDialogComponent>,
    private restaurantService: RestaurantService
  ) {}

  // Fecha objetivo: siguiente día laborable disponible (se recalcula en ngOnInit)
  tomorrowsDate: Date = this.toMidnight(new Date());

  // Menús calculados para la fecha objetivo
  menuStats = {
    reservedMenus: 0,
    extraMenus: 0,
    normalMenus: 0,
    celiacMenus: 0,
    totalGuests: 0,
    celiacGuests: 0,
    total: 0,
  };

  // New property to store celiac details
  celiacMenuDetails: string[] = [];

  config: RestaurantConfig = {
    id: 0,
    extra_menus: 0,
    start_date: '',
    end_date: null,
  };

  private _snackBar = inject(MatSnackBar);

  // Loading flags
  isConfigLoaded = false;
  isReservationsLoaded = false;

  get isLoading() {
    return !(this.isConfigLoaded && this.isReservationsLoaded);
  }

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  private blockedDates: BlockedDate[] = [];
  private blockedSet: Set<string> = new Set(); // YYYY-MM-DD de días bloqueados activos

  // ---------- Ciclo de vida ----------
  ngOnInit() {
    // Load blocked dates first so we compute the correct target date,
    // then load the config for that exact date and the reservations.
    this.loadBlockedDatesAndThenReservations();
  }

  // ---------- Carga de datos ----------
  /**
   * Load restaurant config for a specific target date. If no date is
   * provided, it will calculate the next working day (taking blocked days
   * into account) and load the config for that date.
   */
  loadConfig(targetDate?: Date) {
    if (targetDate) {
      this.tomorrowsDate = targetDate;
    } else {
      this.tomorrowsDate = this.getNextWorkingDay(new Date());
    }

    const tomorrow = this.formatYYYYMMDD(this.tomorrowsDate);

    // console.log('Loading config for date:', tomorrow);

    this.restaurantService.getRestaurantConfigByDate(tomorrow).subscribe({
      next: (config) => {
        this.config = config;
        // Update menuStats.extraMenus with the loaded config value
        this.menuStats.extraMenus = Number(this.config?.extra_menus ?? 0);
        // Recalculate the total with the new extra menus value
        this.updateNormalMenus();
        this.isConfigLoaded = true;
      },
      error: (err) => {
        console.error('Error loading restaurant config:', err);
        this.isConfigLoaded = true; // even on error, release loading to avoid spinner stuck
      },
    });
  }

  loadBlockedDatesAndThenReservations() {
    this.restaurantService.getBlockedDays().subscribe({
      next: (blockedDates: BlockedDate[]) => {
        // Guardamos bloqueos activos (no eliminados) en un Set para lookup rápido
        this.blockedDates = blockedDates || [];
        this.blockedSet = new Set(
          this.blockedDates
            .filter((b) => !b.is_deleted && !!b.date)
            .map((b) => b.date.trim())
        );

        // Calcular la siguiente fecha laborable disponible
        this.tomorrowsDate = this.getNextWorkingDay(new Date());

        // Primero cargamos la config para esa fecha (de forma que guests_number
        // devuelto por la API corresponde exactamente a la fecha usada)
        this.loadConfig(this.tomorrowsDate);

        // Con la fecha ya calculada y config solicitada, cargamos reservas
        this.loadReservationsForTargetDate(this.tomorrowsDate);
      },
      error: (error) => {
        console.error('Error loading blocked dates:', error);
        // Si falla, al menos calcula el siguiente laborable sin bloqueos (sólo fin de semana)
        this.tomorrowsDate = this.getNextWorkingDay(new Date());
        this.loadReservationsForTargetDate(this.tomorrowsDate);
      },
    });
  }

  loadReservationsForTargetDate(targetDate: Date) {
    const ymd = this.formatYYYYMMDD(targetDate);
    // console.log('Target YMD:', ymd); // Add this debug line
    this.tomorrowsDate = this.getNextWorkingDay(new Date());
    const tomorrow = this.formatYYYYMMDD(this.tomorrowsDate);

    this.restaurantService.getReservationsParams(tomorrow, tomorrow).subscribe({
      next: (reservations: ReservationRecord[]) => {
        // console.log('All reservations count:', reservations.length); // Add this debug line

        // Filtramos por la fecha objetivo (comparando por Y-M-D)
        const filteredReservations = (reservations || []).filter(
          (reservation) => {
            const resYMD = this.getReservationYMD(reservation.date);
            // console.log(
            //   `Reservation ${reservation.id}: ${resYMD} === ${ymd}?`,
            //   resYMD === ymd
            // ); // Add this debug line
            return resYMD === ymd && !reservation.is_deleted;
          }
        );

        // console.log(
        //   'Filtered reservations count:',
        //   filteredReservations.length
        // ); // Add this debug line
        // console.log(
        //   'Filtered reservations IDs:',
        //   filteredReservations.map((r) => r.id)
        // ); // Add this debug line

        // Calculamos menús con esas reservas
        this.calculateMenuStats(filteredReservations);
        this.isReservationsLoaded = true;
      },
      // ... rest of the code
    });
  }

  // ---------- Lógica de fechas ----------
  private toMidnight(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private isWeekend(d: Date): boolean {
    const day = d.getDay(); // 0 = domingo, 6 = sábado
    return day === 0 || day === 6;
  }

  private formatYYYYMMDD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  }

  private getReservationYMD(dateISO: string): string {
    // date puede venir "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ssZ"
    const [ymd] = (dateISO || '').split('T');
    return ymd;
  }

  private isBlocked(d: Date): boolean {
    const ymd = this.formatYYYYMMDD(d);
    return this.blockedSet.has(ymd);
  }

  private getNextWorkingDay(from: Date): Date {
    // Empezamos desde el día siguiente a 'from'
    let d = this.toMidnight(from);
    d.setDate(d.getDate() + 1);

    // Avanzar mientras sea fin de semana o esté bloqueado
    while (this.isWeekend(d) || this.isBlocked(d)) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  // ---------- Cálculo de menús ----------
  calculateMenuStats(reservations: ReservationRecord[] = []) {
    // Reset básico
    this.menuStats = {
      reservedMenus: 0,
      extraMenus: 0,
      normalMenus: 0,
      celiacMenus: 0,
      totalGuests: 0,
      celiacGuests: 0,
      total: 0,
    };

    // Reset celiac details
    this.celiacMenuDetails = [];

    // 1) Extras planificados para la fecha objetivo (por día)
    const E = Number(this.config?.extra_menus ?? 0);
    this.menuStats.extraMenus = E;

    // 2) Menús reservados (empleado) para esa fecha
    this.menuStats.reservedMenus = reservations.length;

    // 3) Invitados y celíacos
    let totalGuests = 0;
    let celiacGuests = 0;
    let celiacEmployees = 0;

    reservations.forEach((reservation) => {
      // Invitados
      const guestsArr = (reservation.guests || []).filter((g) => !g.is_deleted);

      // Separar invitados regulares de celíacos
      const regularGuests = guestsArr.filter((g) => !g.is_celiac).length;
      const celiacGuestsCount = guestsArr.filter((g) => g.is_celiac).length;

      totalGuests += regularGuests;
      celiacGuests += celiacGuestsCount;

      // Celíacos (empleado)
      if (reservation.is_celiac) {
        celiacEmployees += 1;

        // Add employee celiac comment if exists
        if (reservation.celiac_comment && reservation.celiac_comment.trim()) {
          // Clean HTML tags and HTML entities
          const cleanComment = this.cleanHtmlContent(
            reservation.celiac_comment
          );
          if (cleanComment) {
            this.celiacMenuDetails.push(cleanComment);
          }
        }
      }

      // Add celiac guests comments
      guestsArr.forEach((guest) => {
        if (
          guest.is_celiac &&
          guest.celiac_comment &&
          guest.celiac_comment.trim()
        ) {
          // Clean HTML tags and HTML entities
          const cleanComment = this.cleanHtmlContent(guest.celiac_comment);
          if (cleanComment) {
            this.celiacMenuDetails.push(cleanComment);
          }
        }
      });
    });

    this.menuStats.totalGuests = totalGuests;
    this.menuStats.celiacGuests = celiacGuests;
    this.menuStats.celiacMenus = celiacEmployees;

    // 4) Menús normales = reservados + invitados + extra - celíacos
    this.updateNormalMenus();
  }

  // Helper method to clean HTML content
  private cleanHtmlContent(htmlString: string): string {
    if (!htmlString) return '';

    // Remove HTML tags first
    let cleanText = htmlString.replace(/<[^>]*>/g, '');

    // Decode HTML entities using DOMParser (more reliable than textarea)
    const parser = new DOMParser();
    const decodedDoc = parser.parseFromString(cleanText, 'text/html');
    cleanText = decodedDoc.body.textContent || decodedDoc.body.innerText || '';

    // Additional cleanup for common HTML entities that might remain
    cleanText = cleanText
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\xa0/g, ' ') // Non-breaking space
      .replace(/\u00a0/g, ' ') // Unicode non-breaking space
      .trim();

    // Replace multiple spaces with single space
    cleanText = cleanText.replace(/\s+/g, ' ');

    return cleanText;
  }

  onExtraMenusChange() {
    // Permite editar manualmente extras si lo necesitas en UI
    this.updateNormalMenus();
  }

  private updateNormalMenus() {
    // Menús normales = solo empleados reservados (no celíacos)
    // Los invitados se cuentan por separado en totalGuests y celiacGuests
    this.menuStats.normalMenus =
      this.menuStats.reservedMenus - this.menuStats.celiacMenus;

    // Nunca negativo
    if (this.menuStats.normalMenus < 0) {
      this.menuStats.normalMenus = 0;
    }

    // Calcular total
    this.menuStats.total =
      this.menuStats.normalMenus +
      this.menuStats.celiacMenus +
      this.menuStats.totalGuests +
      this.menuStats.celiacGuests +
      this.menuStats.extraMenus;
  }

  // ---------- UI ----------
  onSendMenus() {
    // Format the date for the email title
    const formattedDate = this.formatDateForEmail(this.tomorrowsDate);
    const currentTimestamp = new Date().toISOString();

    // Nuevo body a enviar con los 7 campos (incluyendo celiac_menu_details):
    const data = {
      date: formattedDate,
      menus: this.menuStats.normalMenus,
      celiacs: this.menuStats.celiacMenus,
      guests: this.menuStats.totalGuests,
      celiac_guests: this.menuStats.celiacGuests,
      extra: this.menuStats.extraMenus,
      total: this.menuStats.total,
      celiac_menu_details: this.celiacMenuDetails,
    };

    // Por ahora solo mostramos en console.log
    // console.log('Data to send:', data);

    this.restaurantService.sendRestaurantMenusByEmail(data).subscribe({
      next: () => {
        this.openSnackBar(
          `Menús enviados correctamente para el ${formattedDate}`
        );
      },
      error: () => {
        this.openSnackBar(`Error al enviar los menús`);
      },
    });

    this.restaurantService
      .postConfigDaily({
        start_date: this.formatYYYYMMDD(this.tomorrowsDate),
        extra_menus: this.menuStats.extraMenus,
        menus_sent: true, // Indicate this is from menu sending
        sent_at: currentTimestamp,
      })
      .subscribe({
        next: () => {
          // console.log('Extras diarios guardados correctamente con menus_sent=true y sent_at');
        },
        error: (err) => {
          console.error('Error guardando extras diarios:', err);
        },
      });

    // Close dialog after "sending"
    this.dialogRef.close(true);
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  private formatDateForEmail(date: Date): string {
    // Format date as dd/MM/yyyy for the email
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}
